/**
 * L'aide au survol (règle de Dove, 08/09). Un seul composant, monté une fois
 * dans App : il écoute le document et lit `data-aide-id`. Après 3 s de survol,
 * de focus ou d'appui long, la bulle explique en langage simple. Échap, le
 * défilement ou la sortie de l'élément la ferment. Une seule bulle à la fois.
 */
import { useEffect, useRef, useState } from "react";
import { AIDES, DELAI_AIDE_MS, type AideId } from "./aides";

interface Etat {
  id: AideId;
  libelle: string;
  x: number;
  y: number;
  haut: boolean;
  flecheX: number;
}

const ID_BULLE = "bulle-aide";

function libelleDe(el: Element): string {
  const t = el.getAttribute("aria-label") ?? (el.textContent ?? "").trim().replace(/\s+/g, " ");
  return t.length > 48 ? `${t.slice(0, 45)}…` : t;
}

function cibleDe(ev: Event): HTMLElement | null {
  const t = ev.target;
  return t instanceof Element ? t.closest<HTMLElement>("[data-aide-id]") : null;
}

/**
 * `AIDES` porte un littéral distinct par entrée (via `as const satisfies`) :
 * TS refuse `.titre` sur l'union tant qu'on ne l'élargit pas explicitement au
 * type commun déclaré dans l'interface. Un seul point de lecture, ici.
 */
function aideDe(id: AideId): { titre?: string; texte: string } {
  return AIDES[id];
}

export function Bulle() {
  const [etat, setEtat] = useState<Etat | null>(null);
  const boite = useRef<HTMLDivElement>(null);

  /**
   * Le fondu : la bulle se monte SANS la classe `on` (opacité 0), qui n'est
   * posée qu'à l'image suivante — sinon la transition CSS n'a jamais d'état de
   * départ et la bulle apparaît d'un coup. La classe est posée à la main, pas
   * par un état React : ça évite un second rendu (et un avertissement `act`).
   */
  useEffect(() => {
    const el = boite.current;
    if (!el) return;
    el.classList.remove("on");
    const image = requestAnimationFrame(() => el.classList.add("on"));
    return () => { cancelAnimationFrame(image); };
  }, [etat]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let courant: HTMLElement | null = null;

    const fermer = () => {
      if (timer) clearTimeout(timer);
      timer = null;
      if (courant) courant.removeAttribute("aria-describedby");
      courant = null;
      setEtat(null);
    };
    const montrer = (el: HTMLElement) => {
      const id = el.getAttribute("data-aide-id") as AideId | null;
      if (!id || !(id in AIDES)) return;
      courant = el;
      el.setAttribute("aria-describedby", ID_BULLE);
      const r = el.getBoundingClientRect();
      const largeur = 320;
      const x = Math.max(8, Math.min(r.left, window.innerWidth - largeur - 8));
      const haut = r.bottom + 120 > window.innerHeight;
      setEtat({
        id,
        libelle: aideDe(id).titre ?? libelleDe(el),
        x,
        y: haut ? r.top - 10 : r.bottom + 10,
        haut,
        flecheX: Math.max(12, Math.min(largeur - 22, r.left + r.width / 2 - x - 5)),
      });
    };
    const armer = (el: HTMLElement) => {
      fermer();
      timer = setTimeout(() => montrer(el), DELAI_AIDE_MS);
    };

    const surEntree = (ev: Event) => { const el = cibleDe(ev); if (el && el !== courant) armer(el); };
    const surSortie = (ev: Event) => {
      const el = cibleDe(ev);
      if (!el) return;
      const vers = (ev as MouseEvent).relatedTarget;
      if (vers instanceof Element && vers.closest("[data-aide-id]") === el) return;
      fermer();
    };
    const surFocus = (ev: Event) => { const el = cibleDe(ev); if (el) armer(el); };
    const surToucher = (ev: Event) => { if ((ev as PointerEvent).pointerType === "touch") { const el = cibleDe(ev); if (el) armer(el); } };
    // TOUTE frappe ferme la bulle et annule le compte à rebours, pas seulement
    // Échap : sinon la bulle s'ouvre par-dessus le champ pendant qu'on écrit,
    // et plus rien ne la referme tant qu'on ne bouge ni souris ni focus.
    const surTouche = () => { fermer(); };

    document.addEventListener("pointerover", surEntree);
    document.addEventListener("pointerout", surSortie);
    document.addEventListener("focusin", surFocus);
    document.addEventListener("focusout", fermer);
    document.addEventListener("pointerdown", surToucher);
    document.addEventListener("keydown", surTouche);
    window.addEventListener("scroll", fermer, true);
    return () => {
      fermer();
      document.removeEventListener("pointerover", surEntree);
      document.removeEventListener("pointerout", surSortie);
      document.removeEventListener("focusin", surFocus);
      document.removeEventListener("focusout", fermer);
      document.removeEventListener("pointerdown", surToucher);
      document.removeEventListener("keydown", surTouche);
      window.removeEventListener("scroll", fermer, true);
    };
  }, []);

  if (!etat) return null;
  const a = AIDES[etat.id];
  return (
    <div
      id={ID_BULLE}
      ref={boite}
      role="tooltip"
      className={`bulle${etat.haut ? " haut" : ""}`}
      style={{ left: etat.x, top: etat.y, transform: etat.haut ? "translateY(-100%)" : undefined, ["--fleche-x" as string]: `${String(etat.flecheX)}px` }}
    >
      <span className="bt-titre">
        Que fait <b>« {etat.libelle} »</b> ?
      </span>
      {a.texte}
    </div>
  );
}
